import { useMemo, FC, useCallback, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, IconButton, InputAdornment, Typography } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { useAuth } from '@frontegg/react'
import theme from 'theme'
import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'
import toast from 'react-hot-toast'

/* Utils */
import axios from '../../../../lib/axios'
import { defaultValues } from './setupIntegration.constants'
import { useAppDispatch } from '../../../../store/hooks'
import { useCallbackPrompt } from '../../../../hooks/useCallbackPrompt'
import { ModalType, openModal } from '../../../../store/modals/modalSlice'
import { hasAnyRole, Role, stringOfNumberedListToArray } from '../../../../lib/utils'
import {
  IntegrationInstance,
  ManifestIntegration,
  ManifestIntegrationConfigSchema,
  ManifestIntegrationSupportedCPI,
  ProductSpec,
  SnowflakeInetgrationSchemaItem,
  ProductSpecConnectionConfig
} from '../../../../models'
import { ProductsServiceV4 } from '../../../../services'
import { buildCpiActivationOffer, getConnectionConfig, hasConnectionConfigChanged } from './utils'

/* Components */
import {
  IntegrationsContainer,
  EditIntegrationContainer,
  popperSx,
  hollowButtonDisabledSx,
  hollowButtonSx
} from './setupIntegrationInstance.styles'
import { ThemeButton } from '../../../components/buttons'
import { ClearAllIcon, VisibilityOffIcon, VisibilityOnIcon } from '../../../components/svg'
import ControlledTextFieldCustomLabel from '../../../components/form-components/ControlledTextFieldCustomLabel'
import ControlledCustomTrendsSelect from '../../../components/form-components/ControlledCustomTrendsSelect'
import { ConnectionDetailsTooltip } from './components/ConnectionDetailsTooltip'
import TestConnectionButton from './components/TestConnectionButton'
import { PermissionTooltip } from '../../../components/tooltip'
import IntegrationHeader from '../../../components/header/IntegrationHeader'
import { HotToast } from '../../../components/toast/hot-toast'

export enum TestStatus {
  unchecked = 'unchecked',
  successful = 'successful',
  failed = 'failed'
}

interface Props {
  setupType: 'add' | 'edit'
}

const SetupIntegrationInstance: FC<Props> = ({ setupType }) => {
  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user: authUser } = useAuth()
  const closeNotification = useRef<any>(null)
  const [integration, setIntegration] = useState<ManifestIntegration | null>(null)
  const [integrationLoading, setIntegrationLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)
  const [testStatus, setTestStatus] = useState<TestStatus>(TestStatus.unchecked)
  const [initialValues, setInitialValues] = useState<Record<string, string>>(defaultValues)
  const [configSchemaV2, setSchemaV2] = useState<SnowflakeInetgrationSchemaItem[]>([])
  const [instructions, setInstructions] = useState<RegExpMatchArray | null>(null)
  const [visibleValues, setVisibleValues] = useState<Record<string, boolean>>({})
  const [instructionsOpen, setInstructionsOpen] = useState(true)
  const [savedValues, setSavedValues] = useState(defaultValues)
  const [allowSave, setAllowSave] = useState(false)
  const [nameHelperText, setNameHelperText] = useState('')
  const [allCpis, setAllCpis] = useState([])
  const [productSpec, setProductSpec] = useState<ProductSpec | null>(null)
  const [instance, setInstance] = useState<any>(null)
  const [existingInstances, setExistingInstances] = useState<IntegrationInstance[]>([])

  const isDuplicateName = (name: string) =>
    existingInstances.some(
      (inst) =>
        inst.name.toLowerCase() === name.toLowerCase() &&
        inst.integration_name === params.integrationName &&
        (setupType === 'add' || inst.id !== params.instanceId)
    )

  const getNameError = (name: string) => {
    if (!name.length) {
      return 'Please enter a name for the instance'
    }
    if (!name.match(/^[0-9a-zA-Z\-_]+$/)) {
      return 'Only alphanumeric, dash and hyphen characters are allowed'
    }
    if (isDuplicateName(name)) {
      return 'An instance with this name already exists'
    }

    return ''
  }

  const handleSubmit = async () => {
    if (!integration) return

    setLoading(true)
    setTesting(true)

    const configValues = getConnectionConfig(formik.values)
    const shouldIncludeConnectionConfig = setupType === 'add' || hasConnectionConfigChanged(formik.values, savedValues)

    try {
      let res
      if (setupType === 'add') {
        if (integration.is_v2 && productSpec) {
          res = await axios.post('/api/v4/products/check_connection', {
            product_check: {
              product_slug: productSpec.manifest.slug,
              connection_config: configValues
            }
          })
        } else {
          const payload = {
            integrationName: integration?.name || '',
            config: configValues
          }
          res = await axios.post(`/api/v3/integrations/${integration?.name || ''}/check_connection`, payload)
        }
      } else {
        if (integration.is_v2 && productSpec) {
          if (shouldIncludeConnectionConfig) {
            res = await axios.post('/api/v4/products/check_connection', {
              product_check: {
                product_slug: productSpec.manifest.slug,
                connection_config: configValues
              }
            })
          } else {
            res = await axios.post('/api/v4/products/check_connection', {
              product_check: {
                instance_name: instance?.instance_name
              }
            })
          }
        } else {
          const payload: any = {
            name: formik.values.name
          }

          if (shouldIncludeConnectionConfig) {
            payload.config = configValues
          }

          res = await axios.post(`/api/v3/integrations/config/${params.instanceId}/check_connection`, payload)
        }
      }

      if (integration.is_v2 && productSpec) {
        if (res.status !== 200) {
          setTestStatus(TestStatus.failed)
          toast.error(() => <HotToast text="Connection test failed. Check your network and settings." />)
        } else {
          setTestStatus(TestStatus.successful)
          toast.success(() => (
            <HotToast
              text="Connection Test was successful!"
              description="Click 'Save' to complete integration process"
            />
          ))
          setAllowSave(true)
        }
      } else {
        if (res.data.status === 'failed') {
          setTestStatus(TestStatus.failed)
          toast.error(() => <HotToast text="Connection test failed. Check your network and settings." />)
        } else {
          setTestStatus(TestStatus.successful)
          toast.success(() => (
            <HotToast
              text="Connection Test was successful!"
              description="Click 'Save' to complete integration process"
            />
          ))
          setAllowSave(true)
        }
      }
    } catch (e) {
      console.error(e)
      setTestStatus(TestStatus.failed)
      toast.error(() => (
        <HotToast
          text="Test Failed"
          description={(e as any).response?.data?.message || 'Please double check your configuration'}
        />
      ))
    } finally {
      setLoading(false)
      setTesting(false)
    }
  }

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit
  })

  const discardDisabled = useMemo(() => {
    let disabled = true
    const values = formik.values

    /* ONYXIA-4839
     Removing this so discard can be disabled when user initially enters the edit integration page
     */
    // if (instructionsOpen) {
    //   return false
    // }

    for (const [key, value] of Object.entries(values)) {
      if (value !== (savedValues as any)[key]) {
        disabled = false
      }
    }

    return disabled
  }, [formik.values, savedValues, instructionsOpen])

  const [enablePrompt, setEnablePrompt] = useState(discardDisabled)

  useEffect(() => {
    if (setupType === 'add') {
      setEnablePrompt(false)
    } else {
      setEnablePrompt(!discardDisabled)
    }
  }, [discardDisabled, setupType])

  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(enablePrompt)

  useEffect(() => {
    if (showPrompt) {
      dispatch(
        openModal({
          type: ModalType.cancelSetupIntegrationChanges,
          props: { confirmNavigation, cancelNavigation }
        })
      )
    }
  }, [showPrompt])

  useEffect(() => {
    if (initialValues) {
      const valuesArr = Object.keys(initialValues)
      const valuesObj: Record<string, boolean> = {}
      valuesArr.forEach((key) => (valuesObj[key] = false))
      // setVisibleValues(valuesObj)
    }
  }, [initialValues])

  const stepsImages: { [key: string]: number[] } = {
    abnormal: [1, 2, 3],
    'armis-centrix': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    automox: [1, 2, 3, 4, 5, 6, 7],
    axonius: [1, 2, 3, 4, 5],
    bitsight_tprm: [1, 2, 3, 5, 6, 7],
    checkmarx_one: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    checkpoint_harmony: [1, 2, 3, 4],
    cisco_duo: [1, 2, 3, 4, 5],
    cisco_umbrella: [1, 2, 3, 4],
    cloudflare: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    cofense_phishme: [1, 2, 3, 4, 5],
    crowdstrike_cspm: [1, 2, 3, 4],
    crowdstrike_falcon: [1, 2, 3, 4, 5, 6, 7],
    crowdstrike_identity_iam: [1, 2, 3, 4],
    crowdstrike_spotlight: [2, 3, 4, 5],
    expel_workbench: [1, 2, 3, 4],
    google_workspace: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
    ivanti_itsm: [2],
    jamf_pro: [1, 2, 3, 4, 5, 6, 7, 8],
    jumpcloud: [1, 2, 3, 4, 5],
    knowbe4_kmsat: [1, 2, 3, 4, 5, 6],
    knowbe4_phisher: [2, 3, 4, 5, 6, 7],
    microsoft_defender365: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
    microsoft_defenderforcloud: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    microsoft_defenderforemail: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    microsoft_defendervm: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    microsoft_entraid: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    microsoft_intune: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    microsoft_sentinel: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
    mimecast: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    okta: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
    orca: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    proofpoint_sat: [2, 3, 4, 5],
    proofpoint_tap: [1, 2, 3, 4],
    qualys_vm: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    rapid7_appsec: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    rapid7_vm: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    sentinelone_singularity: [1, 2, 3, 4, 5, 6, 7, 8],
    servicenow_cmdb: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    sophos: [1, 2, 3, 4],
    tanium: [1, 2, 3, 4, 5, 6],
    tenable_vm: [2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 15, 16, 17],
    vmware_workspaceone: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    wiz: [2, 4, 5],
    zenity: [1, 2, 3, 4, 5]
  }

  useEffect(() => {
    if (!integration) return

    if (integration?.documentation?.path) {
      import(`./instructions/${integration?.documentation?.path}_instructions.md`)
        .then((res) => {
          fetch(res.default)
            .then((res) => res.text())
            .then((res) => {
              setInstructions(stringOfNumberedListToArray(res, integration.name))
            })
            .catch((err) => console.error(err))
            .finally(() => {
              setIntegrationLoading(false)
            })
        })
        .catch((err) => console.error(err))
    } else {
      setTimeout(() => {
        setIntegrationLoading(false)
      }, 200)
    }
  }, [integration])

  function transformPropertiesToArray(connectionConfig: ProductSpecConnectionConfig) {
    const { properties, required } = connectionConfig

    const allProperties = Object.entries(properties).map(([key, value]) => {
      return {
        name: key,
        required: required.includes(key),
        ...(value as any)
      }
    })

    return allProperties.filter((n) => n.required === true)
  }

  const fetchProductSpec = async (productName: string) => {
    const res = await axios.post('/api/v4/products/spec', {
      product_spec: {
        product_slug: productName
      }
    })

    if (res.status === 200) {
      const productSpec = res.data
      setProductSpec(productSpec)

      return productSpec
    }
  }

  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await axios.get('/api/v3/integrations/supported')

      if (res.status === 200) {
        const { integrations } = res.data
        const integration = integrations.find((n: ManifestIntegration) => n.name === params.integrationName)
        setIntegration(integration)

        if (integration.is_v2) {
          const productSpec: ProductSpec = await fetchProductSpec(integration.name)
          const schema = transformPropertiesToArray(productSpec.connection_config)
          setSchemaV2(schema)

          const instanceValues: Record<string, string> = {
            name: productSpec.manifest.slug,
            description: productSpec.manifest.description || ''
          }
          const visibleValues: any = {}
          if (schema) {
            schema.forEach((n) => {
              instanceValues[n.name] = ''
              if (n.airbyte_secret) {
                visibleValues[n.name] = !!n.airbyte_secret
              }
            })
          }
          setVisibleValues(visibleValues)
          setInitialValues(instanceValues)
          formik.setValues(instanceValues)
          setSavedValues(instanceValues as any)
        } else {
          let currentName = integration.name
          if (integration.name === 'microsoft_defender365') {
            currentName = integration.display_name.toLowerCase().replaceAll(' ', '_')
          }
          const instanceValues: Record<string, string> = {
            name: currentName,
            description: integration.description || ''
          }
          const visibleValues: any = {}
          if (integration.config.schema) {
            integration.config.schema.forEach((n: Record<string, any>) => {
              instanceValues[n.name] = ''
              if (n.airbyte_secret) {
                visibleValues[n.name] = !!n.airbyte_secret
              }
            })
          }
          setVisibleValues(visibleValues)
          setInitialValues(instanceValues)
          formik.setValues(instanceValues)
          setSavedValues(instanceValues as any)
        }
      }
    } catch (e) {
      console.error(e)
      if ((e as any).response?.data?.message === 'Unauthorized') {
        window.location.replace('/')
      }
    }
  }, [])

  const fetchInstanceConfig = useCallback(async () => {
    try {
      const res = await axios.get(`/api/v3/integrations/config/${params.instanceId}`)

      if (res.status === 200) {
        const { data: instance } = res.data
        const { config } = instance
        let instanceValues: Record<string, string> = {
          name: instance.name,
          description: instance.description || ''
        }
        setInstance(instance)
        if (config.schema) {
          config.schema.forEach((n: Record<string, any>) => {
            instanceValues[n.name] = ''
          })
        } else {
          instanceValues = {
            ...instanceValues,
            ...config
          }
        }
        setInitialValues(instanceValues)
        formik.setValues(instanceValues)
        setSavedValues(instanceValues as any)
      }
    } catch (e) {
      console.error(e)
    }
  }, [])

  const fetchAllCpis = async () => {
    try {
      const res = await axios.get('/api/v3/cpis')

      if (res.status === 200) {
        setAllCpis(res.data.data.cpis)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchExistingInstances = async () => {
    try {
      const res = await axios.get<{ instances: IntegrationInstance[] }>(
        '/api/v3/integrations/configured?loadStatus=true'
      )
      if (res.status === 200) {
        setExistingInstances(res.data.instances)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const fetchData = async () => {
    setDataLoading(true)

    try {
      await fetchIntegrations()
      await fetchExistingInstances()
      if (setupType === 'edit' && params.instanceId) {
        await fetchInstanceConfig()
      }
      if (setupType === 'add') {
        await fetchAllCpis()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setDataLoading(false)
    }
  }

  useEffect(() => {
    fetchData().catch((e) => e)
  }, [setupType, params.instanceId])

  useEffect(() => {
    if (integration && integration.config.schema) {
      if (setupType === 'add') {
        const initialValues: Record<string, string> = {}
        if (integration.config.schema) {
          integration.config.schema.forEach((n) => {
            initialValues[n.name] = ''
          })
        }
        setInitialValues(initialValues)
        formik.setValues({ ...formik.values, ...initialValues })
        setSavedValues({ ...formik.values, ...initialValues } as any)
      }
    }
  }, [integration])

  /* The name is prefilled from the integration, so a duplicate has to surface without the user typing. */
  useEffect(() => {
    const error = getNameError(formik.values.name)

    if (error) {
      setNameHelperText(error)
      formik.setFieldError('name', error)
      setAllowSave(false)
    } else if (nameHelperText) {
      setNameHelperText('')
      formik.setFieldError('name', undefined)
    }
  }, [formik.values.name, existingInstances])

  const handleSave = async () => {
    if (!integration) return
    if (integration.is_v2 && !productSpec) return
    if (isDuplicateName(formik.values.name)) {
      toast.error(() => (
        <HotToast
          text="An instance with this name already exists"
          description={`${integration.display_name} integration with name '${formik.values.name}' already exists`}
        />
      ))
      return
    }

    clearTimeout(closeNotification.current)
    setLoading(true)

    const configValues = getConnectionConfig(formik.values)
    const shouldIncludeConnectionConfig = setupType === 'add' || hasConnectionConfigChanged(formik.values, savedValues)

    try {
      if (setupType === 'edit') {
        const payload: any = {
          name: formik.values.name,
          description: formik.values.description
        }

        if (shouldIncludeConnectionConfig) {
          payload.config = configValues
        }

        if (integration.is_v2 && productSpec) {
          const requestPayload: any = {
            product_update: {
              display_name: formik.values.name,
              description: formik.values.description
            }
          }

          if (shouldIncludeConnectionConfig) {
            requestPayload.product_update.connection_config = configValues
          }

          await axios.put(`/api/v4/products/${instance?.instance_name || ''}`, requestPayload)
        } else {
          await axios.put(`/api/v3/integrations/config/${params.instanceId}`, payload)
        }
        toast.success(() => (
          <HotToast text="Integration changes were saved successfully!" description={`${integration?.display_name}`} />
        ))
        setAllowSave(false)
        setEnablePrompt(false)
        setTimeout(() => {
          navigate('/settings/integrations/data-sources')
        }, 100)
      } else {
        const payload = {
          integrationName: integration?.name || '',
          description: formik.values.description,
          name: formik.values.name,
          config: configValues
        }

        let configRes: any

        if (integration.is_v2 && productSpec) {
          configRes = await axios.post('/api/v4/products/create', {
            product_create: {
              product_slug: productSpec.manifest.slug,
              display_name: formik.values.name,
              description: formik.values.description,
              connection_config: configValues
            }
          })
        } else {
          configRes = await axios.post('/api/v3/integrations/config', payload)
        }

        if (configRes.status.toString().includes('20')) {
          toast.success(() => (
            <HotToast
              text="Integration changes were saved successfully!"
              description={`${integration?.display_name}`}
            />
          ))

          const addCpisCallback = async (cpis: string[]) => {
            const payload = {
              cpis
            }

            let res: any
            if (integration.is_v2 && productSpec) {
              /* Add CPIs to integration */
              res = await axios.post(`/api/v4/products/${configRes.data.instance_name || ''}/configure-metrics`, {
                tenant_slug: 'tenant_slug',
                metrics_slugs: cpis
              })
            } else {
              /* Add CPIs to integration */
              res = await axios.post(`/api/v3/integrations/config/${configRes.data?.data?.id || ''}/cpis`, payload)
            }

            if (res.status.toString().includes('20')) {
              if (integration.is_v2 && productSpec) {
                toast.success(() => (
                  <HotToast text={`${integration?.display_name} was successfully added to ${cpis.length} CPIs.`} />
                ))
                navigate('/settings/integrations/data-sources')

                return
              }
              const id = res.data.id

              try {
                /* Check async result of adding CPIs to integration */
                const resp = await axios.get(`/api/v3/async/result/${id}`)

                if (resp.status === 200) {
                  if (resp.data.status === 'COMPLETED') {
                    toast.success(() => (
                      <HotToast text={`${integration?.display_name} was successfully added to ${cpis.length} CPIs.`} />
                    ))
                    navigate('/settings/integrations/data-sources')
                  } else {
                    navigate('/settings/integrations/data-sources', {
                      state: { jobId: id, integrationName: integration?.display_name || '', cpis: cpis.length }
                    })
                  }
                }
              } catch (err) {
                toast.error(() => (
                  <HotToast text={`Failed to add ${integration?.display_name} to ${cpis.length} CPIs.`} />
                ))
              }
            }
          }

          /* v2 instances are offered what the platform says they can activate; a
           * failed fetch fails soft — setup is saved and the CPIs stay activatable
           * from the CPI library. The legacy offer keeps the manifest join. */
          let cpisList: ManifestIntegrationSupportedCPI[] | null
          if (integration.is_v2 && productSpec) {
            try {
              const metrics = await ProductsServiceV4.getCompatibleMetrics(configRes.data.instance_name || '')
              cpisList = buildCpiActivationOffer(metrics)
            } catch (e) {
              console.error(e)
              toast.error(() => (
                <HotToast
                  text="Failed to load CPIs for activation"
                  description="CPIs can be activated later from the CPI Library"
                />
              ))
              cpisList = null
            }
          } else {
            cpisList = (integration?.supported_cpis || []).map((c) => {
              const found: any = allCpis.find((n: any) => n.name === c.name)
              return {
                ...c,
                display_name: found?.display_name || c.name,
                title: found?.title || c.title
              }
            })
          }

          if (cpisList) {
            dispatch(
              openModal({
                type: ModalType.addCpisToIntegration,
                props: {
                  integrationName: formik.values.name || '',
                  cpisList,
                  addCpisCallback
                }
              })
            )
          } else {
            setTimeout(() => navigate('/settings/integrations/data-sources'), 100)
          }
        }
      }

      setSavedValues({
        name: formik.values.name,
        description: formik.values.description,
        ...configValues
      })
      setAllowSave(false)
      setEnablePrompt(false)
    } catch (e) {
      console.error(e)
      setTestStatus(TestStatus.failed)
      toast.error(() => (
        <HotToast
          text={(e as any).response?.data?.message || 'Failed Saving Configuration'}
          description={(e as any).response?.data?.message || 'Please double check your configuration'}
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  const sortIntegrationsSchemaByOrder = (
    items: SnowflakeInetgrationSchemaItem[] | ManifestIntegrationConfigSchema[]
  ) => {
    return items.sort((a, b) => ((a as any).order > (b as any).order ? 1 : -1))
  }

  const getSetupTypeText = () => {
    const firstLetter = setupType.charAt(0)
    const firstLetterCap = firstLetter.toUpperCase()
    const remainingLetters = setupType.slice(1)

    return firstLetterCap + remainingLetters
  }

  const handleExpand = () => {
    setInstructionsOpen(!instructionsOpen)
  }

  const handleDiscard = () => {
    formik.setValues(savedValues)
    setAllowSave(false)
    setNameHelperText('')
    formik.setFieldError('name', undefined)
  }

  const handleClearAll = () => {
    const fields = formik.values
    const newValues: any = {}
    for (const key of Object.keys(fields)) {
      if (key === 'name' || key === 'description') {
        newValues[key] = fields[key]
      } else {
        newValues[key] = ''
      }
    }
    formik.setValues(newValues)
  }

  const testDisabled = useMemo(() => {
    if (!integration) return true

    const values = formik.values
    const schema = configSchemaV2 || integration.config.schema

    if (!values.name || !values.name.length) {
      return true
    }

    let disabled = false
    schema.forEach((item) => {
      if (item.required) {
        const found = values[item.name]
        if (typeof found !== 'undefined') {
          if (item.required && item.type === 'string') {
            if (found.length < 1) {
              disabled = true
            }
          }
        }

        if (!found) {
          disabled = true
        }
      }
    })

    return disabled
  }, [formik.values, integration])

  const noEditPermission = useMemo(() => hasAnyRole([Role.Viewer, Role.Demo], authUser), [authUser])

  if (!integration || dataLoading) {
    return <IntegrationsContainer />
  }
  return (
    <>
      <IntegrationsContainer hasPadding={true}>
        <IntegrationHeader
          handleSave={handleSave}
          handleDiscard={handleDiscard}
          allowSave={allowSave}
          handleBack={() => {
            navigate(-1)
          }}
          headerTitle={`${getSetupTypeText()} ${integration.display_name} Integration Instance`}
          testStatus={testStatus}
          isSubmitting={formik.isSubmitting}
          isValid={formik.isValid}
          loading={loading}
          schema={configSchemaV2 || integration?.config?.schema}
          discardDisabled={discardDisabled}
          noEditPermission={noEditPermission}
        />

        <form autoComplete="off" onSubmit={formik.handleSubmit}>
          <EditIntegrationContainer
            testStatus={testStatus}
            className="sections-wrapper"
            isopen={instructionsOpen ? 'true' : 'false'}
          >
            <Box id="left-side" className="left">
              <Box className="info">
                <Box className="connection-details-wrapper">
                  <Box className="connection-details-description">
                    <Typography className="connection-details-title">Connection Details</Typography>
                    <Typography className="connection-details-text">
                      Provide the necessary information to establish and configure your connection
                    </Typography>
                  </Box>
                  <Box className="connection-details-action">
                    <ConnectionDetailsTooltip title={instructionsOpen ? 'Less information' : 'More information'} arrow>
                      <ThemeButton
                        themevariant="tertiary"
                        className="expand-info-button"
                        onClick={handleExpand}
                        sx={{
                          borderColor: instructionsOpen ? `${theme.colors.cta} !important` : '',

                          '&:hover': {
                            background: theme.colors.cta,

                            svg: {
                              path: {
                                fill: theme.colors.surfaceContainerBackground
                              }
                            }
                          }
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                            fill={theme.colors.cta}
                          />
                        </svg>
                      </ThemeButton>
                    </ConnectionDetailsTooltip>
                  </Box>
                </Box>

                <ControlledTextFieldCustomLabel
                  // name={configSchemaV2.length > 0 ? 'onyxia_product_instance' : 'name'}
                  name="name"
                  label="Name"
                  required
                  onBlur={() => testStatus === TestStatus.failed && setTestStatus(TestStatus.unchecked)}
                  // value={configSchemaV2.length > 0 ? formik.values.onyxia_product_instance : formik.values.name}
                  value={formik.values.name}
                  onChange={(e) => {
                    formik.handleChange(e)

                    if (!getNameError(e.target.value)) {
                      setAllowSave(testStatus !== TestStatus.successful)
                      setTestStatus(TestStatus.unchecked)
                    }
                  }}
                  disabled={loading || formik.isSubmitting || noEditPermission}
                  helperText={nameHelperText}
                />

                <ControlledTextFieldCustomLabel
                  name="description"
                  label="Description (Optional)"
                  value={formik.values.description}
                  onChange={(e) => {
                    // setAllowSave(true)
                    // ONYXIA-4837
                    setAllowSave(testStatus !== TestStatus.successful)
                    setTestStatus(TestStatus.unchecked)
                    formik.handleChange(e)
                  }}
                  onBlur={() => testStatus === TestStatus.failed && setTestStatus(TestStatus.unchecked)}
                  disabled={loading || formik.isSubmitting || noEditPermission}
                />

                {(configSchemaV2.length > 0 ? configSchemaV2 : integration.config.schema) &&
                  sortIntegrationsSchemaByOrder(
                    configSchemaV2.length > 0 ? configSchemaV2 : integration.config.schema
                  ).map((n) => {
                    const hasToggleVisibility = !!n.airbyte_secret
                    if (n.enum && n.enum.length > 0) {
                      return (
                        <ControlledCustomTrendsSelect
                          options={n.enum}
                          placeholder={n.title}
                          label="Region"
                          labeltext={n.title}
                          value={formik.values[n.name]}
                          onChange={(e) => {
                            setAllowSave(false)
                            setTestStatus(TestStatus.unchecked)
                            formik.handleChange(e)
                          }}
                          name={n.name}
                          disabled={noEditPermission}
                          multiple={false}
                          fullWidth
                          autoComplete="off"
                        />
                      )
                    }
                    return (
                      <ControlledTextFieldCustomLabel
                        data-private
                        key={n.name}
                        name={n.name}
                        label={n.title}
                        required={n.required}
                        placeholder={`Enter ${n.title}`}
                        onBlur={() => testStatus === TestStatus.failed && setTestStatus(TestStatus.unchecked)}
                        autoComplete="off"
                        value={formik.values[n.name]}
                        onChange={(e) => {
                          setAllowSave(false)
                          setTestStatus(TestStatus.unchecked)
                          formik.handleChange(e)
                        }}
                        type={
                          n.type === 'integer'
                            ? 'number'
                            : hasToggleVisibility
                            ? visibleValues[n.name]
                              ? 'text'
                              : 'password'
                            : 'text'
                        }
                        endAdornment={
                          hasToggleVisibility && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle Client Secret visibility"
                                onClick={() =>
                                  setVisibleValues((pre) => ({ ...pre, [n.name]: !visibleValues[n.name] }))
                                }
                                edge="end"
                              >
                                {!visibleValues[n.name] ? <VisibilityOffIcon /> : <VisibilityOnIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                        disabled={loading || formik.isSubmitting || noEditPermission}
                      />
                    )
                  })}
              </Box>

              <Box className="actions">
                <TestConnectionButton
                  formik={formik}
                  testDisabled={testDisabled}
                  configSchemaV2={configSchemaV2}
                  loading={loading}
                  nameHelperText={nameHelperText}
                  integration={integration}
                  testing={testing}
                  testStatus={testStatus}
                  noPermission={noEditPermission}
                />

                {noEditPermission ? (
                  <PermissionTooltip
                    PopperProps={{ sx: popperSx }}
                    title={
                      <>
                        <Box className="tooltip-wrapper-permissions">
                          <Typography className="tooltip-text">Insufficient permissions</Typography>
                        </Box>
                      </>
                    }
                  >
                    <ThemeButton
                      themevariant="hollow"
                      sx={hollowButtonDisabledSx}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      disabled={false}
                    >
                      <>
                        <ClearAllIcon />
                        Clear All
                      </>
                    </ThemeButton>
                  </PermissionTooltip>
                ) : (
                  <ThemeButton
                    themevariant="hollow"
                    type="button"
                    sx={hollowButtonSx}
                    onClick={handleClearAll}
                    disabled={loading || formik.isSubmitting}
                  >
                    <>
                      <ClearAllIcon />
                      Clear All
                    </>
                  </ThemeButton>
                )}
              </Box>
            </Box>

            {instructionsOpen && (
              <Box id="right-side" className="right">
                <Box className="right-text-wrapper">
                  <Typography className="instructions-text">Instructions</Typography>
                  {integrationLoading ? (
                    ''
                  ) : (
                    <>
                      {!instructions?.length ? (
                        <Typography className="instructions-description">N/A</Typography>
                      ) : (
                        <Typography className="instructions-description">
                          Follow these steps to successfully set up and configure your connection
                        </Typography>
                      )}
                    </>
                  )}
                </Box>

                {instructions?.map((instruction, index) => {
                  const thisInstruction = instruction.slice(3).replaceAll("'.", '.')
                  const idx = index + 1

                  return (
                    <Box className="instructions-inner" key={instruction}>
                      <Typography className="step-text">STEP {idx}</Typography>

                      {stepsImages[integration.documentation.path] &&
                      stepsImages[integration.documentation.path].includes(idx) ? (
                        <Zoom>
                          <img
                            id={`step_${idx}_image`}
                            alt={`step_${idx}_image`}
                            src={`/${integration.documentation.path}_step_${idx}.png`}
                            width="160px"
                            className="instructions-image"
                            style={{
                              objectFit: 'cover',
                              objectPosition: 'center',
                              borderRadius: theme.attributes.borderRadius.tertiary
                            }}
                          />
                        </Zoom>
                      ) : (
                        <Box className="empty-image" />
                      )}

                      <Box
                        className={`instructions-right-side ${
                          thisInstruction.includes('Depending on the location of your KnowBe4 account')
                            ? 'empty-row-instruction'
                            : ''
                        }`}
                      >
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{thisInstruction}</ReactMarkdown>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </EditIntegrationContainer>
        </form>
      </IntegrationsContainer>
    </>
  )
}

export default SetupIntegrationInstance
